// @givmo/fundraisers-module — the sending-domain card's words (EMC-WEB3), after Jonathan's mockups 10–12
// (Email Campaign plan, scratchpad 2026-10-27-1445-mockup-walkthrough). The package's copy rail
// (`copy-law.test.ts`) reads this file with every other.
//
// Two places say less than the mockups, because what the mockups show isn't built (both ruled by the
// orchestrator, 2026-10-30, and listed for Jonathan in the PR): the page checks the records while it is
// open, never on its own, and "Send records" opens the person's own email app with the records written
// in, from their own mailbox.

export const SENDING_DOMAIN_COPY = {
  // Before anything is set up (mockup 10).
  setupTitle: (domain: string | null) =>
    domain ? `Send from ${domain}` : 'Send from your own domain',
  // RE-WORDED (the polish-1 user test: "Givmo needs to send as kenchild.org first" read awkwardly, and
  // as if nothing could be written yet).
  setupBody: (domain: string | null, orgName: string) =>
    `Before emails can go out, ${
      domain ?? 'your domain'
    } needs three DNS records. You can write emails while they’re added. This sets up sending for all of ${orgName}’s emails.`,
  setupAction: (domain: string | null) => (domain ? `Set up ${domain}` : 'Set up your domain'),

  // The address picker.
  dialogTitle: 'Choose your sending address',
  dialogBody:
    'Emails go out from this address at your own domain. Next, Givmo shows the three records to add to it.',
  addressLabel: 'Email address',
  localPlaceholder: 'appeal',
  domainPlaceholder: 'yourdomain.org',
  addressHelper:
    'Your organization’s own domain. Gmail, Yahoo and other mailbox addresses can’t be used.',
  nameLabel: 'Name people see',
  replyLabel: 'Replies go to',
  // RE-WORDED (the KCC dev build, 2026-10-31): the field no longer starts with the signed-in person's
  // own address, so it says whose inbox belongs here.
  replyPlaceholder: (domain: string | null) => `giving@${domain ?? 'yourdomain.org'}`,
  replyHelper: 'Donors’ replies go here. Use an inbox your organization reads, not a personal one.',
  postalLabel: 'Mailing address',
  // RE-WORDED (the polish-1 user test: where the footer's address comes from).
  postalHelper: 'Every email’s footer shows this address, as email law requires.',
  continue: 'Continue',
  save: 'Save',
  cancel: 'Cancel',
  missing: 'Fill in every field to continue.',
  missingPostal: 'Add the mailing address to continue.',
  changeDomainNote: (domain: string) =>
    `Emails already scheduled wait until ${domain}’s records are in place, then go out from it.`,
  changeDomainAction: (domain: string) => `Switch to ${domain}`,
  failed: 'Givmo couldn’t save this. Please try again.',

  // The records (mockup 11).
  recordsTitle: (domain: string) => `Send from ${domain}`,
  /** `zone`: where the records go, the registrable domain (`givmocharitable.org` for `mail.…`). */
  recordsBody: (zone: string) =>
    `Add these three CNAME records where ${zone}’s DNS is managed. Givmo checks for them every few minutes while this page is open.`,
  fromLead: 'Emails will come from',
  change: 'Change',
  forAll: (orgName: string) => `This sets up sending for all of ${orgName}’s emails.`,
  type: 'Type',
  name: 'Name',
  value: 'Value',
  status: 'Status',
  copyName: (host: string) => `Copy name ${host}`,
  copyValue: (host: string) => `Copy value for ${host}`,
  copied: 'Copied',
  copyFailed: 'Couldn’t copy it. Select it and copy it instead.',
  zoneNote: (zone: string) =>
    `Most DNS screens add ${zone} for you, so the name copies without it.`,
  zoneNoteFull: (zone: string) => `Names copy in full, for DNS screens that don’t add ${zone}.`,
  fullNamesAction: 'Copy full names instead',
  shortNamesAction: 'Copy short names instead',
  found: 'Found',
  notFoundYet: 'Not found yet',
  missingRecord: 'Missing',

  // DMARC (plan §2.8: two DMARC records switch DMARC off, so Givmo adds one only where none exists).
  /** `owner`: the domain the record was found at, the organizational domain's for a subdomain. */
  dmarcExists: (domain: string, owner: string = domain) =>
    owner.toLowerCase() === domain.toLowerCase()
      ? `${domain} already has a DMARC record. Givmo found it and is leaving it alone.`
      : `${owner}’s DMARC record covers ${domain}. Givmo found it and is leaving it alone.`,
  dmarcNeeded: (domain: string) =>
    `${domain} has no DMARC record yet. Add this one too: it tells inboxes what to do with mail that isn’t really from ${domain}.`,
  dmarcUnknown: (domain: string) =>
    `Givmo couldn’t look up ${domain}’s DMARC record. Check records to look again.`,

  // Passing the records on (the mockup's "Send these to whoever manages kenchild.org").
  sendLead: (zone: string) => `Send these to whoever manages ${zone}`,
  sendPlaceholder: (zone: string) => `it@${zone}`,
  sendAction: 'Send records',
  sendNote: 'Opens your email app with the records written in.',
  copyAll: 'Copy all records',
  copiedAll: 'Records copied',
  mailSubject: (domain: string) => `DNS records for ${domain} email`,
  mailIntro: (domain: string, zone: string, orgName: string) =>
    `Hi,\n\nCould you add these records to ${zone}’s DNS? They let ${orgName} send its email from ${domain} through Givmo.`,
  mailRecord: (index: number, type: string, short: string, host: string, value: string) =>
    `${index}. ${type.toUpperCase()} record\n   Name: ${short}   (in full: ${host})\n   Value: ${value}`,
  mailDmarcExists: (domain: string, owner: string = domain) =>
    owner.toLowerCase() === domain.toLowerCase()
      ? `${domain} already has a DMARC record: please leave it as it is.`
      : `${owner} already has a DMARC record, which covers ${domain}: please leave it as it is.`,
  mailOutro: 'Thank you!',

  // Checking.
  check: 'Check records',
  checking: 'Checking…',
  /** "Last checked at 10:42 AM." (the polish-1 user test: a clock time says a check ran). */
  lastChecked: (when: string) => `Last checked ${when}.`,
  notYet: 'Not all of them are there yet. DNS changes can take a few hours to show.',
  readyToast: (domain: string) => `${domain} is ready to send from`,
  checkFailed: 'Givmo couldn’t check the records just now. Please try again.',

  // Ready (mockup 12).
  readyTitle: (address: string) => `Sending as ${address}`,
  readyBody: (orgName: string, domain: string) =>
    `All three CNAME records are in place. Every ${orgName} campaign email now comes from ${domain}.`,
  /** NEW (the polish-1 user test: "where did the footer's address come from?"). */
  footerAddress: (address: string) => `Every email’s footer shows ${address}.`,
  /** NEW (the polish-1 user test): "Change" beside a working address asks first, and says what it does. */
  changeTitle: 'Change the sending address?',
  changeBody: (address: string) =>
    `Emails go out from ${address} now. Next you can change the name people see, where replies go and the footer’s address. A new domain needs its own DNS records, and scheduled emails wait until they’re in place.`,
  changeConfirm: 'Change it',
  changeKeep: 'Keep it',

  // Paused: a record went missing after the domain was ready (mockup 19's words).
  lapsedTitle: (domain: string) => `Sending from ${domain} is paused`,
  lapsedBody: (domain: string) =>
    `A record Givmo needs is missing from ${domain}’s DNS. Emails wait until it’s back.`,

  // The provider no longer has the domain.
  goneTitle: (domain: string) => `${domain} needs setting up again`,
  goneBody: (domain: string) =>
    `Givmo’s email provider no longer has ${domain}. Set it up again to get new records.`,
  goneAction: 'Set up again',

  // Read failures.
  loadFailed: 'Couldn’t load your sending domain',
  loadFailedBody: 'Givmo couldn’t load it just now. Please try again.',
  retry: 'Try again',

  // The minimal Emails tab body (EMC-WEB1 replaces it).
  tab: 'Emails',
  emptyTitle: 'No emails yet',
  // RE-WORDED (the polish-1 user test: writing never waits on the domain; only scheduling does).
  emptyBeforeDomain: (domain: string | null) =>
    `Write the campaign’s emails now. You can schedule them once ${domain ?? 'your domain'} is set up.`,
  emptyAfterDomain: 'Write the campaign’s emails and put them on its dates.',
} as const;
